/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * DatePicker — A custom, unified calendar-style date picker.
 * Replaces browser-default <input type="date"> across the entire platform.
 * Zero external dependencies — built with React + Tailwind only.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { cn } from './utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

/** Parse "YYYY-MM-DD" → Date (local timezone). Returns null if invalid. */
function parseYMD(str: string): Date | null {
  if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Format Date → "YYYY-MM-DD" */
function fmtYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Check if two dates are the same calendar day. */
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Build a 6-row × 7-col grid of Date objects for the given month. */
function buildCalendarGrid(year: number, month: number): Date[][] {
  const firstDay = new Date(year, month, 1);
  const startOffset = firstDay.getDay(); // 0 = Sunday
  const start = new Date(year, month, 1 - startOffset);

  const grid: Date[][] = [];
  let cursor = new Date(start);
  for (let week = 0; week < 6; week++) {
    const row: Date[] = [];
    for (let day = 0; day < 7; day++) {
      row.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    grid.push(row);
  }
  return grid;
}

// ─── Component Props ──────────────────────────────────────────────────────────

export interface DatePickerProps {
  /** Current value in "YYYY-MM-DD" format */
  value: string;
  /** Called with new "YYYY-MM-DD" string on selection */
  onChange: (value: string) => void;
  /** Minimum selectable date in "YYYY-MM-DD" format */
  min?: string;
  /** Maximum selectable date in "YYYY-MM-DD" format */
  max?: string;
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Whether the input is required */
  required?: boolean;
  /** Additional class names for the outer wrapper */
  className?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

// ─── DatePicker Component ─────────────────────────────────────────────────────

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date…',
  required,
  className,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const parsed = parseYMD(value);
  const minDate = min ? parseYMD(min) : null;
  const maxDate = max ? parseYMD(max) : null;

  // Calendar view state — defaults to the selected date's month or today
  const [viewYear, setViewYear] = useState(() => (parsed ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (parsed ?? new Date()).getMonth());

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [positioned, setPositioned] = useState(false);

  // Sync view when value changes externally
  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]);

  // Position the portal dropdown synchronously before paint to prevent flicker
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) { setPositioned(false); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 340; // approximate height of calendar
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: 280,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
    setPositioned(true);
  }, [open, viewMonth, viewYear]);

  // Close on outside click — check both container and portal dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const goPrev = useCallback(() => {
    setViewMonth(prev => {
      if (prev === 0) { setViewYear(y => y - 1); return 11; }
      return prev - 1;
    });
  }, []);

  const goNext = useCallback(() => {
    setViewMonth(prev => {
      if (prev === 11) { setViewYear(y => y + 1); return 0; }
      return prev + 1;
    });
  }, []);

  const selectDate = (d: Date) => {
    onChange(fmtYMD(d));
    setOpen(false);
  };

  const isDisabledDate = (d: Date): boolean => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const grid = buildCalendarGrid(viewYear, viewMonth);
  const today = new Date();

  // Display text
  const displayText = parsed
    ? parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
    : '';

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Input */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left transition-all",
          "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
          "focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400",
          "hover:border-slate-300",
          disabled && "opacity-50 cursor-not-allowed bg-slate-50",
          !displayText && "text-slate-400",
          open && "ring-2 ring-indigo-500/20 border-indigo-400",
        )}
      >
        <Calendar size={14} className="text-zinc-400 shrink-0" />
        <span className="flex-1 truncate">{displayText || placeholder}</span>
      </button>

      {/* Hidden native input for form validation */}
      {required && (
        <input
          type="text"
          required
          value={value}
          onChange={() => {}}
          tabIndex={-1}
          className="sr-only"
          aria-hidden="true"
        />
      )}

      {/* Calendar Dropdown — rendered via portal to escape overflow:hidden containers */}
      {open && createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className={cn(
          'bg-white border border-slate-200 rounded-lg shadow-lg shadow-slate-200/60 p-3',
          positioned ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}>
          {/* Month/Year Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goPrev}
              className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-500 hover:text-zinc-900"
            >
              <ChevronLeft size={16} />
            </button>
            <p className="text-sm font-bold text-zinc-900">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </p>
            <button
              type="button"
              onClick={goNext}
              className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-500 hover:text-zinc-900"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_HEADERS.map(dh => (
              <div key={dh} className="text-center text-[9px] font-bold text-zinc-400 uppercase tracking-widest py-1">
                {dh}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {grid.map((week, wi) =>
              week.map((day, di) => {
                const inMonth = day.getMonth() === viewMonth;
                const isSelected = parsed ? sameDay(day, parsed) : false;
                const isToday = sameDay(day, today);
                const isOff = isDisabledDate(day);

                return (
                  <button
                    key={`${wi}-${di}`}
                    type="button"
                    disabled={isOff}
                    onClick={() => !isOff && selectDate(day)}
                    className={cn(
                      "w-9 h-9 mx-auto flex items-center justify-center rounded-lg text-xs font-medium transition-all",
                      !inMonth && "text-zinc-300",
                      inMonth && !isSelected && !isToday && "text-slate-700 hover:bg-indigo-50",
                      isToday && !isSelected && "bg-indigo-50 text-indigo-700 font-bold",
                      isSelected && "bg-indigo-600 text-white font-bold shadow-sm",
                      isOff && "opacity-30 cursor-not-allowed hover:bg-transparent",
                    )}
                  >
                    {day.getDate()}
                  </button>
                );
              })
            )}
          </div>

          {/* Today shortcut */}
          <div className="mt-2 pt-2 border-t border-zinc-100 flex justify-center">
            <button
              type="button"
              onClick={() => {
                const t = new Date();
                if (!isDisabledDate(t)) {
                  selectDate(t);
                } else {
                  setViewYear(t.getFullYear());
                  setViewMonth(t.getMonth());
                }
              }}
              className="text-[10px] font-bold text-zinc-500 hover:text-zinc-900 uppercase tracking-widest transition-colors"
            >
              Today
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
